<script lang="ts">
  import { selectedTeamStore, type SelectedTeam } from "$stores/teams";
  import { link } from "svelte-spa-router";
  import active from "svelte-spa-router/active";
  import { push } from "svelte-spa-router";

  import ContextSwitcher from "$lib/components/identity/ContextSwitcher.svelte";
  import { Button } from "$ui/button";

  import HomeIcon from "$assets/icons/home.svelte";
  import UserIcon from "$assets/icons/user.svelte";
  import AppsIcon from "$assets/icons/apps.svelte";
  import XIcon from "$assets/icons/x.svelte";

  import SettingsIcon from "$assets/icons/settings.svelte";
  import LogoutIcon from "$assets/icons/logout.svelte";

  import Logo from "$assets/icons/logo.svelte";
  import Hamburger from "$assets/icons/hamburger.svelte";
  import { cn } from "$lib/utils/ui";
  import { userStore } from "$lib/stores/user";
  import { getAuth, signOut } from "firebase/auth";
  import { firebaseApp } from "$lib/utils/env";
  import { type Organisation, selectedOrgStore } from "$lib/stores/orgs";
  import { exposedSensitiveDocumentsAvailable } from "$lib/stores/orgExposedSensitiveDocuments";
  import { EarthLock, MonitorUp, Radar, ShieldHalf, FileSearch } from "lucide-svelte";
  import TeamIcon from "$assets/icons/team.svelte";
  import { dataSources } from "$lib/utils/constants";

  const user = $derived($userStore);
  const selectedOrg = $derived($selectedOrgStore);
  const selectedTeam = $derived($selectedTeamStore);

  let mobileNavOpen = $state(false);
  const navLinkClass =
    "nav-item flex items-center gap-3 !font-semibold !px-3 !py-2 -mx-2 [&>svg]:text-gray-700 [&>svg]:stroke-[2px] !text-black dark:!text-white !text-base !justify-start rounded-lg";

  const navSubLinkClass =
    "ml-1 nav-item flex items-center gap-3 !font-semibold !px-3 py-2 -mx-2 [&>svg]:text-gray-700 [&>svg]:stroke-[2px] !text-black dark:!text-white text-sm !justify-start rounded-lg";

  const logout = async () => {
    await signOut(getAuth(await firebaseApp));
    await push("/login");
  };

  const toggleMobileNav = () => {
    mobileNavOpen = !mobileNavOpen;
  };
</script>

<div
  class="sticky lg:top-[--banner-height] nav-height hidden lg:inset-y-0 lg:z-10 lg:flex lg:w-72 lg:flex-col border-r border-general bg-white"
>
  <nav class="h-full flex flex-col px-6 py-8 overflow-auto max-h-screen">
    <div class="flex h-9 w-full max-w-[213px] justify-center items-start">
      <Logo />
    </div>
    <div class="mt-3 pt-2 mb-4 pb-2 relative flex flex-col">
      <ContextSwitcher {selectedOrg} {selectedTeam} />
    </div>
    <div class="flex-1 flex flex-col gap-2">
      {#if selectedTeam !== undefined}
        <a href="/t/{selectedTeam.id}" use:link use:active class={navLinkClass}>
          <HomeIcon />
          Overview
        </a>
        <a href="/t/{selectedTeam.id}/apps" use:link use:active class={navLinkClass}>
          <AppsIcon />
          Apps
        </a>
        <a href="/t/{selectedTeam.id}/members" use:link use:active class={navLinkClass}>
          <TeamIcon />
          Members
        </a>
      {:else if selectedOrg !== undefined && selectedOrg.id}
        <!-- sidebar for an org -->
        <a href={selectedOrg.pagePrefix} use:link use:active class={navLinkClass}>
          <HomeIcon />
          Overview
        </a>
        {#if selectedOrg.onboarding}
          <a href="{selectedOrg.pagePrefix}/connect_data" use:link use:active class={navLinkClass}>
            <MonitorUp />
            Onboarding
          </a>
        {/if}
        <div class={navLinkClass}>
          <ShieldHalf />
          Security
        </div>
        <div class="pl-6 flex-col flex gap-2">
          <a href="{selectedOrg.pagePrefix}/security/permissions-assurance" use:link use:active class={navSubLinkClass}>
            <!-- <EarthLock size="20" /> -->
            Anomalous Permissions
          </a>
          <a
            href="{selectedOrg.pagePrefix}/security/sensitive-data-detection"
            use:link
            use:active
            class={navSubLinkClass}
          >
            <!-- <Radar size="20" /> -->
            Sensitive Data Detection
          </a>
          {#if $exposedSensitiveDocumentsAvailable}
            <!-- Shown only once the org has published exposure metadata the reader may see; the
                 store decides that claim-first, so a reader without the claim issues no read. -->
            <a
              href="{selectedOrg.pagePrefix}/security/exposed-sensitive-documents"
              use:link
              use:active
              class={navSubLinkClass}
            >
              Sensitive Data Exposure
            </a>
          {/if}
        </div>
        <a href="{selectedOrg.pagePrefix}/members" use:link use:active class={navLinkClass}>
          <TeamIcon />
          Members
        </a>
      {:else}
        <!-- sidebar for an org-setup -->
        <a href="/overview" use:link use:active class={navLinkClass}>
          <HomeIcon />
          Overview
        </a>
        <a href="/onboarding" use:link use:active class={navLinkClass}>
          <MonitorUp />
          Onboarding
        </a>
        <a href="/security" use:link use:active class={navLinkClass}>
          <ShieldHalf />
          Security
        </a>
      {/if}
    </div>
    <div class="flex flex-col gap-2 mt-8">
      <!-- <button class={navLinkClass} on:click={() => window.alert("Open Intercom Chat")}>
        <SupportIcon />
        Support
      </button> -->
      {#if selectedOrg !== undefined && !selectedOrg.onboarding}
        <a href={`/o/${selectedOrg.id}/settings`} use:link use:active class={navLinkClass}>
          <SettingsIcon />
          Settings
        </a>
        <a href={`/o/${selectedOrg.id}/settings/custom-info-types`} use:link use:active class={navSubLinkClass}>
          <FileSearch size="18" />
          Custom Data Types
        </a>
      {/if}
      <a href="/profile" use:link use:active class={navLinkClass}>
        <UserIcon />
        Profile
      </a>
    </div>
    <div class="border-t border-general mt-6 pt-6 flex items-center gap-3">
      <div class="flex-1 text-sm overflow-hidden">
        <div class="font-semibold">{user?.name}</div>
        <div class="truncate">{user?.email}</div>
      </div>
      <div class="[&>button>svg]:w-4 h-4 self-baseline mt-1 text-gray-700 hover:text-gray-800 cursor-pointer">
        <Button variant="ghost" on:click={logout}>
          <LogoutIcon />
        </Button>
      </div>
    </div>
  </nav>
</div>

<nav
  class="lg:hidden py-6 px-6 border-b border-general sticky top-0 bg-background-light dark:bg-background-dark z-10 flex justify-between items-center gap-4"
>
  <button class="[&>svg]:w-6" on:click={toggleMobileNav}>
    {#if mobileNavOpen}
      <XIcon />
    {:else}
      <Hamburger />
    {/if}
  </button>
  <div class="flex-1 flex h-9 w-full max-w-[213px] justify-center items-start">
    <Logo />
  </div>
  <div class="w-6" />
</nav>

<div
  class={cn(
    "h-[calc(100svh-73px)] fixed top-[73px] flex flex-col w-[60%] p-4 bg-background-light dark:bg-background-dark z-10 transition-all duration-300 border-r border-general",
    mobileNavOpen ? "left-0 before:!opacity-100 before:delay-200" : "-left-full",
    "before:pointer-events-none before:opacity-0 before:transition-opacity before:delay-100 before:duration-200 before:content-[''] before:bg-black/50 before:w-[200vw] before:h-full before:absolute before:top-0 before:left-full",
  )}
>
  <div class="border-b border-general pt-2 mb-4 pb-2 relative flex flex-col">
    <ContextSwitcher {selectedOrg} {selectedTeam} />
  </div>
  <div class="flex-1 flex flex-col gap-2">
    {#if selectedTeam !== undefined}
      <a href="/t/{selectedTeam.id}" use:link use:active class={navLinkClass}>
        <HomeIcon />
        Overview
      </a>
      <a href="/t/{selectedTeam.id}/apps" use:link use:active class={navLinkClass}>
        <AppsIcon />
        Apps
      </a>
      <a href="/t/{selectedTeam.id}/members" use:link use:active class={navLinkClass}>
        <UserIcon />
        Members
      </a>
    {/if}
    {#if selectedOrg !== undefined}
      <a href="/o/{selectedOrg.id}" use:link use:active class={navLinkClass}>
        <HomeIcon />
        Overview
      </a>
      <a href="/o/{selectedOrg.id}/security" use:link use:active class={navLinkClass}>
        <AppsIcon />
        Security
      </a>
      {#if $exposedSensitiveDocumentsAvailable}
        <a href="/o/{selectedOrg.id}/security/exposed-sensitive-documents" use:link use:active class={navSubLinkClass}>
          Sensitive Data Exposure
        </a>
      {/if}
      <a href="/o/{selectedOrg.id}/members" use:link use:active class={navLinkClass}>
        <UserIcon />
        Members
      </a>
      <a href="/o/{selectedOrg.id}/index" use:link use:active class={navLinkClass}>
        <UserIcon />
        Index
      </a>
    {/if}
  </div>
  {#if selectedOrg !== undefined}
    <div class="flex flex-col gap-2 mt-8">
      <!-- <button class={navLinkClass} on:click={() => window.alert("Open Intercom Chat")}>
      <SupportIcon />
      Support
    </button> -->
      <a href={`/o/${selectedOrg.id}/settings`} use:link use:active class={navLinkClass}>
        <SettingsIcon />
        Settings
      </a>
      <a href={`/o/${selectedOrg.id}/settings/custom-info-types`} use:link use:active class={navSubLinkClass}>
        <FileSearch size="18" />
        Custom Data Types
      </a>
    </div>
  {/if}
  <div class="border-t border-general mt-6 pt-6 flex items-center gap-3">
    <div class="flex-1 text-sm overflow-hidden">
      <div class="font-semibold">{user?.name}</div>
      <div class="truncate">{user?.email}</div>
    </div>
    <div class="[&>button>svg]:w-4 h-4 self-baseline mt-1 text-gray-700 hover:text-gray-800 cursor-pointer">
      <Button variant="ghost" on:click={logout}>
        <LogoutIcon />
      </Button>
    </div>
  </div>
</div>

<style>
  .nav-height {
    height: calc(100vh - var(--banner-height));
  }
</style>
